/**
 * The host-event kit — the "shared control vocabulary" of the host event edit handoff
 * (docs/plans/2026-09-host-event-edit/handoff/README.md), one primitive per row of that
 * table, all on ./hostEvent.css. Every surface on /host/events/:id, the event wizard and
 * the leaf editors it mounts (categories, venue, tiers, promos, questions, links, co-hosts,
 * pages, media) is built from these and nothing else.
 *
 * Nothing here talks to the network. Money and time formatting come from utils/surfaceFormat.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tintFor } from '../../utils/tints';
import './hostEvent.css';

export const RING = 'linear-gradient(90deg,#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D,#FD199F)';
export const ON = `linear-gradient(#000,#000) padding-box, ${RING} border-box`;

/** `$X.XX` en-CA, two decimals; negatives as `−$X.XX` (from a dollar amount, not cents). */
export function usd(n) {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return `${v < 0 ? '−$' : '$'}${Math.abs(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** `SAT OCT 3, 8:00 PM` from an ISO string, a Date, or a datetime-local value. */
export function fmtWhen(s) {
  if (!s) return '';
  const d = s instanceof Date ? s : new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).replace(/\./g, '').toUpperCase();
}

/* ── Cards + section heads ───────────────────────────────────────────── */

/** A white card: no border, no radius, no shadow. `rise` plays the enter animation. */
export function Card({ children, rise = false, black = false, center = false, gap, pad, className = '', style, testId, as: Tag = 'section', ...rest }) {
  return (
    <Tag className={`hev-card ${className}`} data-rise={rise ? '' : undefined} data-black={black ? '' : undefined} data-center={center ? '' : undefined} data-gap={gap} data-pad={pad} style={style} data-testid={testId} {...rest}>
      {children}
    </Tag>
  );
}

/** Section title + subline with the corner tag (`REQUIRED` / `OPTIONAL` / the money word). */
export function SectionHead({ title, sub, tag, tagTone = 'grey', right }) {
  return (
    <div className="hev-sec">
      <span className="hev-sec-text">
        <span className="hev-sec-title">{title}</span>
        {sub && <span className="hev-sec-sub">{sub}</span>}
      </span>
      {right}
      {tag && <Tag black={tagTone === 'black'} cyan={tagTone === 'cyan'}>{tag}</Tag>}
    </div>
  );
}

export function Tag({ children, black = false, cyan = false, small = false, className = '', ...rest }) {
  return <span className={`hev-tag ${className}`} data-black={black ? '' : undefined} data-cyan={cyan ? '' : undefined} data-small={small ? '' : undefined} {...rest}>{children}</span>;
}

/* ── Fields ──────────────────────────────────────────────────────────── */

/**
 * Label + control + helper/error. `required` draws the magenta `*`; `optional` the
 * `· OPTIONAL` suffix; `lockTag` the ` · LOCKED` suffix. `as="label"` wraps the control in a
 * <label> so clicking the text focuses it; use "div" when the control is a group.
 */
export function Field({ label, required = false, optional = false, lockTag = false, small = false, hint, hintAbove, error, children, as: Tag = 'label', htmlFor, className = '', testId, id, style, tag = null, tagTone = null, quote = null }) {
  return (
    <Tag className={`hev-field ${className}`} htmlFor={Tag === 'label' ? htmlFor : undefined} data-testid={testId} id={id} style={style}>
      {label && (
        <span className="hev-label" data-small={small ? '' : undefined} style={tag ? { display: 'flex', alignItems: 'center', gap: 8 } : undefined}>
          <span>
            {label}
            {required && <span className="hev-req"> *</span>}
            {optional && !tag && <span className="hev-opt"> · OPTIONAL</span>}
            {lockTag && <span> · LOCKED</span>}
          </span>
          {/* Plan detection (Oct 2026): where this value came from — FROM CHAT · MATCHED ON MAP · CHECK · SUGGESTED · NEEDED · OPTIONAL · DEFAULT. */}
          {tag && <span className="hev-field-tag" data-field-tag={tag} style={tagTone ? { background: tagTone.bg, color: tagTone.fg } : undefined}>{tag}</span>}
        </span>
      )}
      {quote && <span className="hev-quote">“{quote.quote}”{quote.src ? <span className="hev-quote-src"> · {quote.src}</span> : null}</span>}
      {hintAbove && <span className="hev-help">{hintAbove}</span>}
      {children}
      {error && <span className="hev-err" role="alert">{error}</span>}
      {hint && <span className="hev-help">{hint}</span>}
    </Tag>
  );
}

/** Pill text input. `size`: lg (52px) · md (48) · sm (44) · xs (40). `white` inside grey sub-panels. */
export function TextInput({ size = 'md', white = false, error = false, className = '', ...rest }) {
  return (
    <input
      className={`hev-input ${className}`}
      data-lg={size === 'lg' ? '' : undefined}
      data-sm={size === 'sm' ? '' : undefined}
      data-xs={size === 'xs' ? '' : undefined}
      data-white={white ? '' : undefined}
      data-error={error ? '' : undefined}
      {...rest}
    />
  );
}

/** `$` / `%` affixed pill input. `prefix` on the left, `suffix` on the right, or an `icon` node on the left. */
export function AffixInput({ prefix, suffix, icon, style, wrapClassName = '', ...rest }) {
  return (
    <span className={`hev-affix ${wrapClassName}`} data-side={suffix ? 'right' : undefined} data-icon={icon ? '' : undefined} style={style}>
      {icon}
      {prefix && <i>{prefix}</i>}
      <TextInput {...rest} />
      {suffix && <i>{suffix}</i>}
    </span>
  );
}

export function Textarea({ white = false, error = false, className = '', ...rest }) {
  return <textarea className={`hev-textarea ${className}`} data-white={white ? '' : undefined} data-error={error ? '' : undefined} {...rest} />;
}

export function Select({ white = false, className = '', children, ...rest }) {
  return <select className={`hev-select ${className}`} data-white={white ? '' : undefined} {...rest}>{children}</select>;
}

/** The bare search pill: a magnifier and a borderless input. */
export function SearchPill({ white = false, small = false, className = '', inputRef, ...rest }) {
  return (
    <span className={`hev-search ${className}`} data-white={white ? '' : undefined} data-sm={small ? '' : undefined}>
      <SearchGlyph />
      <input ref={inputRef} type="text" {...rest} />
    </span>
  );
}

export function SearchGlyph({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" />
    </svg>
  );
}

export function PinGlyph({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true">
      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}

export function ScanGlyph({ size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="square" aria-hidden="true">
      <path d="M3 8V3h5M16 3h5v5M21 16v5h-5M8 21H3v-5M7 12h10" />
    </svg>
  );
}

export function Help({ children, body = false, caps = false, amber = false, className = '', style }) {
  return <span className={`hev-help ${className}`} data-body={body ? '' : undefined} data-caps={caps ? '' : undefined} data-amber={amber ? '' : undefined} style={style}>{children}</span>;
}

export function ErrorLine({ children }) {
  if (!children) return null;
  return <span className="hev-err" role="alert">{children}</span>;
}

/* ── Choices, toggles, pills ─────────────────────────────────────────── */

/** Radio-style choice card. `on` = the ON background; `dashed` = the PLAN IT FOR ME card. */
export function ChoiceCard({ on = false, dashed = false, label, hint, onClick, disabled = false, testId, ...rest }) {
  return (
    <button type="button" role="radio" aria-checked={!!on} className="hev-choice" data-on={on ? '' : undefined} data-dashed={dashed ? '' : undefined} onClick={onClick} disabled={disabled} data-testid={testId} {...rest}>
      <span className="hev-choice-l">{label}</span>
      {hint && <span className="hev-choice-h">{hint}</span>}
    </button>
  );
}

/** The 34×20 switch — visual only; put it inside a ToggleRow or a button of your own. */
export function Switch({ on = false }) {
  return <span className="hev-switch" data-on={on ? '' : undefined} aria-hidden="true"><i /></span>;
}

/** A whole-row toggle button: title + description on the left, the switch on the right. */
export function ToggleRow({ label, desc, on = false, onToggle, disabled = false, lockTag = false, plain = false, white = false, testId, ...rest }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={typeof label === 'string' ? label : undefined} className="hev-toggle" data-white={white ? '' : undefined} onClick={onToggle} disabled={disabled} data-testid={testId} {...rest}>
      <span className="hev-toggle-text">
        <span className="hev-toggle-t" data-plain={plain ? '' : undefined}>{label}{lockTag ? ' · LOCKED' : ''}</span>
        {desc && <span className="hev-toggle-d">{desc}</span>}
      </span>
      <Switch on={on} />
    </button>
  );
}

/** Segmented pills: `options` = [{ key, label }]. `white` = white off-fill (inside a grey sub-panel). */
export function SegPills({ options = [], value, onChange, disabled = false, white = false, small = false, grow = false, className = '', ariaLabel }) {
  return (
    <span className={`hev-pills ${className}`} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button key={o.key} type="button" role="radio" aria-checked={value === o.key} className="hev-pill" data-on={value === o.key ? '' : undefined} data-white={white ? '' : undefined} data-sm={small ? '' : undefined} data-grow={grow ? '' : undefined} onClick={() => onChange?.(o.key)} disabled={disabled}>
          {o.label}
        </button>
      ))}
    </span>
  );
}

/* ── Sub-panels + rows ───────────────────────────────────────────────── */

/** Grey rounded panel grouping a cluster of fields. `right` is the corner control (`+ ADD …`). */
export function SubPanel({ title, sub, subBody = false, right, children, gap, error = false, className = '', id, testId, style }) {
  return (
    <div className={`hev-sub ${className}`} data-gap={gap} data-error={error ? '' : undefined} id={id} data-testid={testId} style={style}>
      {(title || right) && (
        <div className="hev-sub-head">
          <span className="hev-sub-text">
            {title && <span className="hev-sub-title">{title}</span>}
            {sub && <span className="hev-sub-sub" data-body={subBody ? '' : undefined}>{sub}</span>}
          </span>
          {right}
        </div>
      )}
      {!title && !right && sub && <span className="hev-sub-sub" data-body={subBody ? '' : undefined}>{sub}</span>}
      {children}
    </div>
  );
}

/** White inner block inside a sub-panel (a draft form). `grid` = two columns. */
export function Inner({ children, grid = false, className = '', testId }) {
  return <div className={`hev-inner ${className}`} data-grid={grid ? '' : undefined} data-testid={testId}>{children}</div>;
}

/** A list entry: white (or grey on white) rounded row. */
export function Row({ children, grey = false, round = false, pad, hover = false, className = '', testId, ...rest }) {
  return <div className={`hev-row ${className}`} data-grey={grey ? '' : undefined} data-round={round ? '' : undefined} data-pad={pad} data-hover={hover ? '' : undefined} data-testid={testId} {...rest}>{children}</div>;
}

export function RowText({ title, meta, bold = false, plainMeta = false, children }) {
  return (
    <span className="hev-row-text">
      {title !== undefined && <span className="hev-row-t" data-bold={bold ? '' : undefined}>{title}</span>}
      {meta !== undefined && meta !== null && meta !== '' && <span className="hev-row-m" data-plain={plainMeta ? '' : undefined}>{meta}</span>}
      {children}
    </span>
  );
}

/** The 30px ✕ circle. */
export function RemoveX({ onClick, label = 'Remove', white = false, large = false, disabled = false, testId }) {
  return <button type="button" className="hev-x" data-white={white ? '' : undefined} data-lg={large ? '' : undefined} onClick={onClick} aria-label={label} disabled={disabled} data-testid={testId}>✕</button>;
}

/* ── Buttons ─────────────────────────────────────────────────────────── */

/**
 * variant: primary (black) · secondary (#F2F2F2) · grey (#F5F5F5) · danger · white · dark (#1A1A1A) ·
 *          text (grey text only) · link (white text only) · plain (black text only) ·
 *          ring-white · ring-cyan · ring-black
 * size: xs 30 · sm 36 · md 44 · lg 48 · xl 52 · cta 50
 */
export function Btn({ variant = 'primary', size = 'md', full = false, between = false, self, children, className = '', type = 'button', testId, ...rest }) {
  return (
    <button type={type} className={`hev-btn ${className}`} data-v={variant} data-s={size} data-full={full ? '' : undefined} data-between={between ? '' : undefined} data-self={self} data-testid={testId} {...rest}>
      {children}
    </button>
  );
}

/* ── Notes, empties ──────────────────────────────────────────────────── */

/** `tone`: grey (default) · warning (#FFF8EA) · black · danger. `tag` is the black pill (`LOCKED`, `HEADS UP`, `ESCROW`). */
export function Note({ tag, tone, title, children, small = false, testId, className = '' }) {
  return (
    <div className={`hev-note ${className}`} data-tone={tone} data-small={small ? '' : undefined} data-testid={testId} role={tone === 'danger' ? 'alert' : undefined}>
      {tag && <span className="hev-note-tag" data-small={small ? '' : undefined}>{tag}</span>}
      {title && <span className="hev-note-title">{title}</span>}
      <span style={{ minWidth: 0 }}>{children}</span>
    </div>
  );
}

export function Empty({ title, children, action, bare = false, plain = false, testId }) {
  return (
    <div className="hev-empty" data-bare={bare ? '' : undefined} data-plain={plain ? '' : undefined} data-testid={testId}>
      <span className="hev-empty-t">{title}</span>
      {children && <span className="hev-empty-s">{children}</span>}
      {action}
    </div>
  );
}

/* ── Avatars ─────────────────────────────────────────────────────────── */

/** Round (or `square`) avatar: the photo, else the tinted initial. */
export function Ava({ name = '', imageUrl, size = 40, square = false, tintKey, className = '', style }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <span className={`hev-ava ${className}`} data-square={square ? '' : undefined} style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: imageUrl ? '#f2f2f2' : tintFor(tintKey || name || initial), ...style }} aria-hidden="true">
      {imageUrl ? <img src={imageUrl} alt="" referrerPolicy="no-referrer" /> : initial}
    </span>
  );
}

/* ── Toast ───────────────────────────────────────────────────────────── */

/** `const { toast, say } = useToast(); … <Toast text={toast} />`. Auto-hides after 2.6s. */
export function useToast(ms = 2600) {
  const [toast, setToast] = useState('');
  const t = useRef(null);
  const say = useCallback((text) => {
    window.clearTimeout(t.current);
    setToast(text);
    t.current = window.setTimeout(() => setToast(''), ms);
  }, [ms]);
  useEffect(() => () => window.clearTimeout(t.current), []);
  return { toast, say };
}

export function Toast({ text, testId = 'hev-toast' }) {
  if (!text) return null;
  return <div className="hev-toast" role="status" data-testid={testId}><i />{text}</div>;
}

/* ── Small things ────────────────────────────────────────────────────── */

/** Key / value money line. */
export function KV({ k, v, small = false, color }) {
  return <span className="hev-kv" data-sm={small ? '' : undefined}><span>{k}</span><span style={color ? { color } : undefined}>{v}</span></span>;
}

export function Yours({ value, small = false, testId }) {
  return <span className="hev-yours" data-sm={small ? '' : undefined}><span>YOURS</span><span data-testid={testId}>{value}</span></span>;
}

/** Progress bar: magenta on the #FBD5FB track (`black` for the tickets bar). */
export function Track({ pct = 0, black = false }) {
  return <span className="hev-track" data-black={black ? '' : undefined} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}><i style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} /></span>;
}
